// Live updates: one SignalR connection to /hubs/tenant with the token (or staff key) on the request. The live
// snapshot is patched by the server's live.patch events; for everything else the server only says what changed and
// the portal re-fetches the affected queries. Pings go both ways every 5 s, so a connection that died without
// closing is given up after 12 s of silence and reconnected. While disconnected, the live snapshot is read every
// 2 s and other queries every 5 s, and the connection is tried again every 5 s until it is back.
import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { getConfig } from "../config";
import type { HubEvent, LiveData, LivePatch } from "../api/types";
import { applyLivePatch } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";

// The live pages (the Queue, the Overview's lists, Needs you, the upload cards) read the live snapshot, which the
// hub's live.patch events keep current: invoice events don't re-fetch them. What else an event affects is fetched
// again at most once per family's window, and a fetch already running is left to finish (scale-fix.md).
const AFFECTS: Record<string, string[][]> = {
  "invoice.changed": [["invoices"], ["invoice"], ["dashboard"], ["needs"], ["ops"]],
  "queue.changed": [["dashboard"], ["ops"]],
  "batch.changed": [["batches"], ["dashboard"], ["invoices"]],
  "settings.changed": [["settings"], ["me"], ["dashboard"], ["syncore"], ["intake"], ["batches"], ["live"]],
  "worker.health": [["dashboard"], ["ops"]],
  "tenant.changed": [["me"], ["dashboard"], ["staff"], ["ops"]],
  "scaling.changed": [["ops"]],
  "switch.changed": [["ops"], ["dashboard"]],
  "upload.changed": [["uploads"], ["invoices"], ["dashboard"], ["needs"]],
};

/** The shortest gap between two re-fetches of one family; the invoice lists, read from SQL, wait a little longer. */
const WINDOW_MS: Record<string, number> = { invoices: 2000, ops: 2000 };
const DEFAULT_WINDOW_MS = 1000;

const RETRY_MS = 5000;
/** Each side pings every 5 s; this much silence from the server means the connection is gone. */
const SERVER_TIMEOUT_MS = 12_000;
const KEEP_ALIVE_MS = 5000;

/** The last live.patch number seen per tenant, and which API instance numbered it. */
const lastPatch = new Map<string, { instance: string; seq: number }>();

export type HubState = "connected" | "connecting" | "disconnected";

export function useHub(tenantId: string | null, enabled = true): HubState {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<HubState>("connecting");
  const connection = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const url = new URL(`${getConfig().apiUrl}/hubs/tenant`);
    if (tenantId) url.searchParams.set("tenantId", tenantId);
    const hub = new HubConnectionBuilder()
      .withUrl(url.toString(), {
        accessTokenFactory: async () => (await auth.getToken()) ?? "",
        // The staff key travels as a header, which only long polling can carry from a browser.
        headers: auth.staffKey ? { "X-Staff-Key": auth.staffKey } : undefined,
        transport: auth.staffKey ? HttpTransportType.LongPolling : undefined,
      })
      .withAutomaticReconnect([0, 1000, 2000, 5000, 5000])
      .withServerTimeout(SERVER_TIMEOUT_MS)
      .withKeepAliveInterval(KEEP_ALIVE_MS)
      .configureLogging(LogLevel.Warning)
      .build();
    connection.current = hub;
    let stopped = false;
    let retry: number | null = null;

    const start = () => {
      if (stopped) return;
      setState("connecting");
      hub.start()
        .then(() => { if (!stopped) { lastPatch.clear(); setState("connected"); void queryClient.invalidateQueries(); } })
        .catch(() => { if (!stopped) { setState("disconnected"); retry = window.setTimeout(start, RETRY_MS); } });
    };

    // Throttled invalidation: each family (and each invoice's own query) is re-fetched at once when an event asks,
    // then at most once per its window however many more arrive, with one last re-fetch at the window's end for
    // what arrived during it. An event about one invoice refreshes that invoice's own query, not every open one.
    const lastRun = new Map<string, number>();
    const trailing = new Map<string, number>();
    const invalidate = (slot: string, run: () => void, windowMs: number) => {
      if (trailing.has(slot)) return;
      const wait = (lastRun.get(slot) ?? 0) + windowMs - Date.now();
      const go = () => { trailing.delete(slot); lastRun.set(slot, Date.now()); run(); };
      if (wait <= 0) go();
      else trailing.set(slot, window.setTimeout(go, wait));
    };
    const refetchFamily = (key: string[]) => invalidate(key.join("/"), () => void queryClient.invalidateQueries({ queryKey: key }, { cancelRefetch: false }), WINDOW_MS[key[0]!] ?? DEFAULT_WINDOW_MS);
    const refetchInvoice = (id: string) => invalidate("invoice/" + id, () => void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "invoice" && q.queryKey[2] === id }, { cancelRefetch: false }), WINDOW_MS.invoice!);
    hub.on("event", (message: HubEvent) => {
      if (message.name === "live.patch") {
        if (!message.tenantId) return;
        const patch = message.payload as unknown as LivePatch;
        queryClient.setQueryData<LiveData>(["live", message.tenantId], (data) => applyLivePatch(data, patch));
        // Patches are numbered by the API instance that sent them: a gap means one never arrived, so the snapshot is read again.
        const last = lastPatch.get(message.tenantId);
        lastPatch.set(message.tenantId, { instance: patch.instance, seq: patch.seq });
        if (last && last.instance === patch.instance && patch.seq !== last.seq + 1) void queryClient.invalidateQueries({ queryKey: ["live", message.tenantId] });
        return;
      }
      const keys = AFFECTS[message.name] ?? [["dashboard"]];
      const invoiceId = typeof message.payload?.invoiceId === "string" ? message.payload.invoiceId : null;
      for (const key of keys) {
        if (key[0] === "invoice") { if (invoiceId) refetchInvoice(invoiceId); }
        else refetchFamily(key);
      }
    });
    hub.onreconnecting(() => setState("connecting"));
    hub.onreconnected(() => {
      lastPatch.clear();
      setState("connected");
      void queryClient.invalidateQueries();
    });
    // The automatic reconnects gave up (a deployment restarting the API takes longer than they wait): keep trying
    // every few seconds rather than polling for the rest of the session.
    hub.onclose(() => { if (!stopped) { setState("disconnected"); retry = window.setTimeout(start, RETRY_MS); } });
    start();
    return () => {
      stopped = true;
      for (const t of trailing.values()) window.clearTimeout(t);
      if (retry) window.clearTimeout(retry);
      if (hub.state !== HubConnectionState.Disconnected) void hub.stop();
      connection.current = null;
    };
  }, [auth, tenantId, enabled, queryClient]);

  return state;
}

/** Polling interval for queries: live events make polling unnecessary; otherwise every 5 s. */
export function refetchInterval(hub: HubState): number | false {
  return hub === "connected" ? false : 5000;
}
